import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  Chip,
  Avatar,
  Button,
  useTheme,
  alpha,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  LinearProgress,
  Collapse,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PictureAsPdf as PdfIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Inventory as InventoryIcon,
  Science as ScienceIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FileDownload as DownloadIcon,
  Settings as SettingsIcon,
  Print as PrintIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as industryApi from '../services/industry';
import type { Industry } from '../services/industry';
import logoLight from '../assets/logo-black.png';
import ValueChainTable from '../components/valueChain/ValueChainTable';

const MotionCard = motion(Card);
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: '8px 0',
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  '& .MuiCheckbox-root': {
    borderRadius: 8,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    }
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    borderRadius: 8,
    padding: '4px 8px',
    margin: '4px -8px',
    transition: 'all 0.2s ease'
  }
}));

const IndustriesReportPage = () => {
  const theme = useTheme();
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedSubIndustries, setSelectedSubIndustries] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    insights: true,
    valueChain: true,
    supportEcosystem: false,
    tabs: false,
    subIndustries: false
  });

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await industryApi.getAllIndustries({
          includeSubIndustries: true,
          includeValueChain: true,
          includeSupportEcosystem: true
        });
        setIndustries(data);
      } catch (err) {
        console.error('Error fetching industries:', err);
        setError('Failed to load industries data');
      } finally {
        setLoading(false);
      }
    };

    fetchIndustries();
  }, []);

  const [selectedSections, setSelectedSections] = useState({
    // Industry Insights
    summary: false,
    description: false,
    shortDescription: false,
    growthRate: false,
    marketSize: false,
    investmentOpportunities: false,
    keyMetrics: false,
    growthData: false,
    tags: false,

    // Value Chain & Ecosystem
    valueChainTable: false,

    supportEcosystem: false,
    supportEcosystemSummary: false,
    supportEcosystemTalent: false,
    supportEcosystemFunding: false,
    supportEcosystemSupplyChain: false,
    supportEcosystemPlayers: false,

    // Tabs (Deep Content)
    marketOverview: false,
    legalInfrastructure: false,
    trends: false,
    incentives: false,

    // Sub-Industries
    includeSubIndustries: false
  });

  const availableSubIndustries = useMemo(() => {
    if (selectedIndustries.length !== 1) return [];
    const industry = industries.find(ind => ind.id === selectedIndustries[0]);
    return industry?.hasSubIndustries && industry.subIndustries ? industry.subIndustries : [];
  }, [selectedIndustries, industries]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(industries.map(ind => ind.category).filter(Boolean)));
    return uniqueCategories;
  }, [industries]);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Localization': return '#6366f1';
      case 'Service & Infrastructure': return '#06b6d4';
      case 'Mature Industries': return '#10b981';
      default: return '#6366f1';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Localization': return <BusinessIcon />;
      case 'Service & Infrastructure': return <InventoryIcon />;
      case 'Mature Industries': return <ScienceIcon />;
      default: return <BusinessIcon />;
    }
  };

  const handleIndustryChange = (event: any) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setSelectedIndustries(value);
    setSelectedSubIndustries([]);
  };

  const handleSubIndustryChange = (event: any) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setSelectedSubIndustries(value);
  };

  const handleSectionChange = (section: keyof typeof selectedSections) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: event.target.checked
    }));
  };

  const toggleSectionExpansion = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isValidForGeneration = () => {
    return selectedIndustries.length > 0 && Object.values(selectedSections).some(selected => selected);
  };

  const getSelectedIndustriesData = () => {
    return industries.filter(ind => selectedIndustries.includes(ind.id));
  };

  const getSelectedSubIndustriesData = () => {
    if (selectedIndustries.length !== 1 || selectedSubIndustries.length === 0) return [];
    const industry = industries.find(ind => ind.id === selectedIndustries[0]);
    if (!industry?.subIndustries) return [];
    return industry.subIndustries.filter(sub => selectedSubIndustries.includes(sub.id));
  };

  const generatePDF = async () => {
    if (!isValidForGeneration()) return;
    
    setIsGenerating(true);
    
    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');
      
      const selectedIndustriesData = getSelectedIndustriesData();
      const selectedSubIndustriesData = getSelectedSubIndustriesData();
      const currentDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      const doc = new jsPDF();
      let yPosition = 20;
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace: number): void => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = 20;
        }
      };

      // Helper function to add text with word wrapping
      interface AddTextOptions {
        fontSize?: number;
        maxWidth?: number;
        fontStyle?: 'normal' | 'bold';
        color?: [number, number, number];
      }

      const addText = (text: string, x: number, y: number, options: AddTextOptions = {}): number => {
        const fontSize = options.fontSize || 10;
        const maxWidth = options.maxWidth || 170;
        const fontStyle = options.fontStyle || 'normal';
        const color = options.color || [0, 0, 0];
        
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        doc.setTextColor(...color);
        
        const lines: string[] = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return lines.length * (fontSize * 0.35);
      };

      // Add branded header to each page
      const addHeader = () => {
        // Brand gradient header background
        doc.setFillColor(37, 99, 235); // Primary blue
        doc.rect(0, 0, pageWidth, 35, 'F');

        // Purple gradient overlay
        doc.setFillColor(120, 70, 200);
        doc.rect(pageWidth * 0.5, 0, pageWidth * 0.5, 35, 'F');

        // VALUE CHAIN GLOBAL branding - large and prominent
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('VALUE CHAIN GLOBAL', margin, 15);

        // Tagline
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('Connecting Industries • Empowering Growth', margin, 22);

        // Vision 2030 badge on the right
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(1);
        doc.roundedRect(pageWidth - margin - 45, 8, 43, 18, 2, 2, 'FD');

        doc.setTextColor(37, 99, 235);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('SAUDI', pageWidth - margin - 39, 14);
        doc.text('VISION 2030', pageWidth - margin - 42, 20);

        // Decorative bottom border
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(2);
        doc.line(0, 33, pageWidth, 33);

        // Reset colors
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');

        return 45; // Return starting Y position after header
      };

      // COVER PAGE - Fully Branded
      yPosition = addHeader();

      // Large VALUE CHAIN GLOBAL branding box
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 45, 5, 5, 'F');

      // White text in branded box
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(32);
      doc.setFont('helvetica', 'bold');
      doc.text('VALUE CHAIN GLOBAL', pageWidth / 2, yPosition + 18, { align: 'center' });

      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('Industry Intelligence & Analysis Platform', pageWidth / 2, yPosition + 32, { align: 'center' });

      yPosition += 60;

      // Report title card
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(2);
      doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 65, 5, 5, 'FD');

      // Left accent bar
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, 6, 65, 5, 5, 'F');

      // Right accent bar
      doc.setFillColor(147, 51, 234);
      doc.roundedRect(pageWidth - margin - 6, yPosition, 6, 65, 5, 5, 'F');

      yPosition += 20;

      // Report title
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('INDUSTRY ANALYSIS', pageWidth / 2, yPosition, { align: 'center' });

      doc.setFontSize(20);
      doc.setTextColor(147, 51, 234);
      doc.text('REPORT', pageWidth / 2, yPosition + 15, { align: 'center' });

      doc.setFontSize(13);
      doc.setTextColor(75, 85, 99);
      doc.setFont('helvetica', 'normal');
      doc.text('Kingdom of Saudi Arabia • Vision 2030', pageWidth / 2, yPosition + 32, { align: 'center' });
      
      // Report summary box with modern design
      yPosition += 105;

      // Add modern card-style box with gradient border effect
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(1);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 65, 4, 4, 'FD');

      // Add accent bar on the left
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, 4, 65, 2, 2, 'F');

      yPosition += 18;
      doc.setFontSize(15);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      doc.text('Report Summary', margin + 12, yPosition);
      
      yPosition += 15;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      
      const summaryLines = [
        `Industries Analyzed: ${selectedIndustriesData.length}`,
        `Sub-industries: ${selectedSubIndustriesData.length}`,
        `Report Sections: ${Object.values(selectedSections).filter(Boolean).length}`,
        `Total Pages: Estimated ${Math.ceil(selectedIndustriesData.length * 2.5)}`
      ];
      
      summaryLines.forEach((line, index) => {
        doc.text(`• ${line}`, margin + 10, yPosition + (index * 8));
      });

      // Selected Industries list
      yPosition += 50;
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('Selected Industries', margin, yPosition);
      
      yPosition += 15;
      selectedIndustriesData.forEach((industry, index) => {
        if (industry.name) {
          // Industry item with bullet
          doc.setFillColor(37, 99, 235);
          doc.circle(margin + 3, yPosition - 2, 2, 'F');
          
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(31, 41, 55);
          doc.text(industry.name, margin + 10, yPosition);
          
          if (industry.category) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(107, 114, 128);
            doc.text(`(${industry.category})`, margin + 10 + doc.getTextWidth(industry.name) + 5, yPosition);
          }
          
          yPosition += 12;
        }
      });

      // Industry Detail Pages
      selectedIndustriesData.forEach((industry, industryIndex) => {
        doc.addPage();
        yPosition = addHeader();

        // Modern industry page header with gradient effect
        doc.setFillColor(249, 250, 251);
        doc.rect(0, yPosition - 5, pageWidth, 50, 'F');

        // Accent bar on the left
        doc.setFillColor(37, 99, 235);
        doc.rect(0, yPosition - 5, 5, 50, 'F');

        // Add subtle decorative element with light purple
        doc.setFillColor(240, 230, 255);
        doc.circle(pageWidth - 30, yPosition + 20, 40, 'F');

        doc.setFontSize(26);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(37, 99, 235);
        if (industry.name) {
          doc.text(industry.name, margin + 5, yPosition + 15);
        }

        if (industry.category) {
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(147, 51, 234);
          doc.text(industry.category.toUpperCase(), margin + 5, yPosition + 28);
        }

        // Decorative underline
        doc.setDrawColor(37, 99, 235);
        doc.setLineWidth(2);
        doc.line(margin + 5, yPosition + 33, margin + 70, yPosition + 33);

        yPosition += 60;

        // Key metrics cards with modern design
        if (selectedSections.marketSize || selectedSections.growthRate || selectedSections.investmentOpportunities) {
          const metricsData = [];
          if (selectedSections.marketSize && industry.marketSize) {
            metricsData.push({ label: 'Market Size', value: `$${industry.marketSize}`, color: [34, 197, 94] });
          }
          if (selectedSections.growthRate && industry.growthRate !== undefined) {
            metricsData.push({ label: 'Growth Rate', value: `${industry.growthRate}%`, color: [59, 130, 246] });
          }
          if (selectedSections.investmentOpportunities && industry.investmentOpportunities !== undefined) {
            metricsData.push({ label: 'Opportunities', value: `${industry.investmentOpportunities}`, color: [168, 85, 247] });
          }

          const cardWidth = (pageWidth - 3 * margin) / metricsData.length;
          metricsData.forEach((metric, index) => {
            const cardX = margin + index * (cardWidth + 10);

            // Modern card with gradient border effect
            doc.setFillColor(255, 255, 255);
            doc.setDrawColor(metric.color[0], metric.color[1], metric.color[2]);
            doc.setLineWidth(1.5);
            doc.roundedRect(cardX, yPosition, cardWidth, 35, 4, 4, 'FD');

            // Accent bar at the top with light color
            const lightColor = metric.color.map(c => Math.min(255, c + 200));
            doc.setFillColor(lightColor[0], lightColor[1], lightColor[2]);
            doc.roundedRect(cardX, yPosition, cardWidth, 6, 4, 4, 'F');

            // Metric value with shadow effect
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(metric.color[0], metric.color[1], metric.color[2]);
            doc.text(String(metric.value), cardX + cardWidth/2, yPosition + 18, { align: 'center' });

            // Metric label
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(107, 114, 128);
            doc.text(metric.label, cardX + cardWidth/2, yPosition + 28, { align: 'center' });
          });

          yPosition += 50;
        }

        // Description section with modern card design
        if (selectedSections.description && industry.description) {
          checkPageBreak(40);

          // Section title with gradient underline
          doc.setFontSize(15);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(37, 99, 235);
          doc.text('Industry Overview', margin, yPosition);

          // Gradient underline
          doc.setDrawColor(37, 99, 235);
          doc.setLineWidth(2);
          doc.line(margin, yPosition + 2, margin + 50, yPosition + 2);

          yPosition += 15;

          // Content box with subtle background
          const contentHeight = Math.ceil(doc.splitTextToSize(industry.description, 160).length * 4);
          doc.setFillColor(248, 250, 252);
          doc.roundedRect(margin, yPosition - 5, pageWidth - 2 * margin, contentHeight + 10, 3, 3, 'F');

          // Left accent bar
          doc.setFillColor(37, 99, 235);
          doc.roundedRect(margin, yPosition - 5, 3, contentHeight + 10, 2, 2, 'F');

          const descHeight = addText(industry.description, margin + 8, yPosition, {
            maxWidth: 160,
            fontSize: 10,
            color: [55, 65, 81]
          });
          yPosition += descHeight + 20;
        }

        // Summary section with modern card design
        if (selectedSections.summary && industry.summary) {
          checkPageBreak(40);

          // Section title with gradient underline
          doc.setFontSize(15);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(147, 51, 234);
          doc.text('Executive Summary', margin, yPosition);

          // Gradient underline
          doc.setDrawColor(147, 51, 234);
          doc.setLineWidth(2);
          doc.line(margin, yPosition + 2, margin + 60, yPosition + 2);

          yPosition += 15;

          // Content box with subtle background
          const contentHeight = Math.ceil(doc.splitTextToSize(industry.summary, 160).length * 4);
          doc.setFillColor(252, 248, 255);
          doc.roundedRect(margin, yPosition - 5, pageWidth - 2 * margin, contentHeight + 10, 3, 3, 'F');

          // Left accent bar
          doc.setFillColor(147, 51, 234);
          doc.roundedRect(margin, yPosition - 5, 3, contentHeight + 10, 2, 2, 'F');

          const summaryHeight = addText(industry.summary, margin + 8, yPosition, {
            maxWidth: 160,
            fontSize: 10,
            color: [55, 65, 81]
          });
          yPosition += summaryHeight + 20;
        }

        // Key Metrics detailed section with modern table
        if (selectedSections.keyMetrics && industry.keyMetrics) {
          checkPageBreak(60);

          // Section title with gradient underline
          doc.setFontSize(15);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(37, 99, 235);
          doc.text('Key Performance Indicators', margin, yPosition);

          // Gradient underline
          doc.setDrawColor(37, 99, 235);
          doc.setLineWidth(2);
          doc.line(margin, yPosition + 2, margin + 80, yPosition + 2);

          yPosition += 18;

          // Create modern metrics table
          const metrics = [
            { label: 'Employment', value: industry.keyMetrics.employment, icon: '👥' },
            { label: 'Export Value', value: industry.keyMetrics.exports, icon: '📦' },
            { label: 'Active Companies', value: industry.keyMetrics.companies, icon: '🏢' }
          ].filter(m => m.value);

          // Table container
          const tableHeight = metrics.length * 14 + 8;
          doc.setFillColor(248, 250, 252);
          doc.roundedRect(margin, yPosition - 3, pageWidth - 2 * margin, tableHeight, 3, 3, 'F');

          metrics.forEach((metric, index) => {
            // Row background
            if (index % 2 === 0) {
              doc.setFillColor(255, 255, 255);
              doc.roundedRect(margin + 2, yPosition, pageWidth - 2 * margin - 4, 13, 2, 2, 'F');
            }

            // Label
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(55, 65, 81);
            doc.text(metric.label, margin + 8, yPosition + 8);

            // Value with colored badge
            const valueText = String(metric.value);
            const valueWidth = doc.getTextWidth(valueText);

            // Badge background with light blue
            doc.setFillColor(220, 235, 255);
            doc.roundedRect(pageWidth - margin - valueWidth - 18, yPosition + 2, valueWidth + 12, 9, 2, 2, 'F');

            doc.setFont('helvetica', 'bold');
            doc.setTextColor(37, 99, 235);
            doc.text(valueText, pageWidth - margin - 12, yPosition + 8, { align: 'right' });

            yPosition += 14;
          });

          yPosition += 15;
        }

        // Growth Data Chart Section with modern design
        if (selectedSections.growthData && industry.growthData && industry.growthData.length > 0) {
          checkPageBreak(90);

          // Section title with gradient underline
          doc.setFontSize(15);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(34, 197, 94);
          doc.text('Growth Trajectory', margin, yPosition);

          // Gradient underline
          doc.setDrawColor(34, 197, 94);
          doc.setLineWidth(2);
          doc.line(margin, yPosition + 2, margin + 55, yPosition + 2);

          yPosition += 20;

          // Modern chart container
          doc.setFillColor(248, 250, 252);
          doc.setDrawColor(203, 213, 225);
          doc.setLineWidth(1);
          doc.roundedRect(margin, yPosition - 5, pageWidth - 2 * margin, 55, 3, 3, 'FD');

          // Chart title
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(100, 116, 139);
          doc.text('Annual Growth Performance', margin + 5, yPosition + 2);

          yPosition += 8;

          // Enhanced bar chart
          const maxValue = Math.max(...industry.growthData.map(d => d.value));
          const chartWidth = pageWidth - 4 * margin - 10;
          const chartHeight = 32;
          const barWidth = chartWidth / industry.growthData.length;

          industry.growthData.forEach((data, index) => {
            const barHeight = (data.value / maxValue) * chartHeight;
            const barX = margin + 15 + (index * barWidth);
            const barY = yPosition + chartHeight - barHeight + 3;

            // Gradient bar effect
            doc.setFillColor(37, 99, 235);
            doc.roundedRect(barX, barY, barWidth * 0.7, barHeight, 1, 1, 'F');

            // Add highlight on top of bar with lighter purple
            doc.setFillColor(200, 180, 240);
            doc.roundedRect(barX, barY, barWidth * 0.7, Math.min(4, barHeight), 1, 1, 'F');

            // Year label
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(100, 116, 139);
            doc.text(String(data.year), barX + (barWidth * 0.35), yPosition + chartHeight + 12, { align: 'center' });

            // Value label on bar
            doc.setFontSize(8);
            doc.setTextColor(37, 99, 235);
            doc.text(String(data.value), barX + (barWidth * 0.35), barY - 2, { align: 'center' });
          });

          yPosition += 75;
        }

        // Value Chain section removed - using print view instead

        // Support Ecosystem section
        if (selectedSections.supportEcosystem && industry.supportEcosystem) {
          checkPageBreak(60);

          // Branded section header
          doc.setFillColor(34, 197, 94);
          doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 24, 4, 4, 'F');

          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('SUPPORT ECOSYSTEM', pageWidth / 2, yPosition + 15, { align: 'center' });

          yPosition += 34;

          Object.entries(industry.supportEcosystem).forEach(([key, section]) => {
            if (section && typeof section === 'object') {
              checkPageBreak(35);

              // Calculate estimated height
              let estimatedHeight = 20; // Base for title
              if (section.programs) estimatedHeight += (section.programs.length * 7) + 5;
              if (section.initiatives) estimatedHeight += (section.initiatives.length * 7) + 5;
              estimatedHeight += 15; // Bottom padding

              // Draw ecosystem card background FIRST
              doc.setFillColor(240, 253, 244);
              doc.setDrawColor(34, 197, 94);
              doc.setLineWidth(1);
              doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, estimatedHeight, 3, 3, 'FD');

              // Left accent
              doc.setFillColor(34, 197, 94);
              doc.roundedRect(margin + 5, yPosition + 3, 4, 18, 3, 3, 'F');

              doc.setFontSize(11);
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(34, 197, 94);
              doc.text(section.title || key.charAt(0).toUpperCase() + key.slice(1), margin + 15, yPosition + 13);

              yPosition += 20;

              // Render arrays
              if (section.programs && Array.isArray(section.programs)) {
                section.programs.forEach((item: any) => {
                  doc.setFillColor(34, 197, 94);
                  doc.circle(margin + 10, yPosition - 1, 1.5, 'F');

                  const itemHeight = addText(String(item), margin + 15, yPosition, {
                    maxWidth: 160,
                    fontSize: 9,
                    color: [55, 65, 81]
                  });
                  yPosition += itemHeight + 5;
                });
              }

              if (section.initiatives && Array.isArray(section.initiatives)) {
                section.initiatives.forEach((item: any) => {
                  doc.setFillColor(34, 197, 94);
                  doc.circle(margin + 10, yPosition - 1, 1.5, 'F');

                  const itemHeight = addText(String(item), margin + 15, yPosition, {
                    maxWidth: 160,
                    fontSize: 9,
                    color: [55, 65, 81]
                  });
                  yPosition += itemHeight + 5;
                });
              }

              yPosition += 10;
            }
          });
        }

        // Market Overview from tabs with modern design
        if (selectedSections.marketOverview && industry.tabs?.marketOverview) {
          const mo = industry.tabs.marketOverview;
          checkPageBreak(60);

          // Section title with gradient underline
          doc.setFontSize(15);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(59, 130, 246);
          doc.text('Market Analysis', margin, yPosition);

          // Gradient underline
          doc.setDrawColor(59, 130, 246);
          doc.setLineWidth(2);
          doc.line(margin, yPosition + 2, margin + 55, yPosition + 2);

          yPosition += 18;

          if (mo.marketPotential && mo.marketPotential.length > 0) {
            // Subsection header
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(37, 99, 235);
            doc.text('Market Potential', margin + 3, yPosition);
            yPosition += 12;

            mo.marketPotential.forEach((item: any) => {
              if (item) {
                // Modern bullet points with icon
                doc.setFillColor(37, 99, 235);
                doc.circle(margin + 7, yPosition - 1.5, 1.5, 'F');

                const itemHeight = addText(item, margin + 12, yPosition, {
                  maxWidth: 160,
                  fontSize: 10,
                  color: [55, 65, 81]
                });
                yPosition += itemHeight + 6;
              }
            });
            yPosition += 10;
          }

          if (mo.criticalStrengths && mo.criticalStrengths.length > 0) {
            checkPageBreak(30);

            // Subsection with green theme
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(34, 197, 94);
            doc.text('Strengths', margin + 3, yPosition);
            yPosition += 12;

            mo.criticalStrengths.forEach((item: any) => {
              if (item) {
                // Green bullet points
                doc.setFillColor(34, 197, 94);
                doc.circle(margin + 7, yPosition - 1.5, 1.5, 'F');

                const itemHeight = addText(item, margin + 12, yPosition, {
                  maxWidth: 160,
                  fontSize: 10,
                  color: [55, 65, 81]
                });
                yPosition += itemHeight + 6;
              }
            });
            yPosition += 10;
          }

          if (mo.criticalGaps && mo.criticalGaps.length > 0) {
            checkPageBreak(30);

            // Subsection with red theme
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(239, 68, 68);
            doc.text('Areas for Development', margin + 3, yPosition);
            yPosition += 12;

            mo.criticalGaps.forEach((item: any) => {
              if (item) {
                // Red bullet points
                doc.setFillColor(239, 68, 68);
                doc.circle(margin + 7, yPosition - 1.5, 1.5, 'F');

                const itemHeight = addText(item, margin + 12, yPosition, {
                  maxWidth: 160,
                  fontSize: 10,
                  color: [55, 65, 81]
                });
                yPosition += itemHeight + 6;
              }
            });
          }
        }

        // Legal Infrastructure section
        if (selectedSections.legalInfrastructure && industry.tabs?.legalInfrastructure) {
          const li = industry.tabs.legalInfrastructure;
          checkPageBreak(50);

          doc.setFillColor(239, 68, 68);
          doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 24, 4, 4, 'F');

          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('LEGAL & REGULATORY INFRASTRUCTURE', pageWidth / 2, yPosition + 15, { align: 'center' });

          yPosition += 34;

          if (li.legal && li.legal.regulations && Array.isArray(li.legal.regulations)) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(239, 68, 68);
            doc.text('Key Regulations', margin + 3, yPosition);
            yPosition += 12;

            li.legal.regulations.forEach((item: any) => {
              doc.setFillColor(239, 68, 68);
              doc.circle(margin + 7, yPosition - 1.5, 1.5, 'F');

              const itemHeight = addText(String(item), margin + 12, yPosition, {
                maxWidth: 160,
                fontSize: 10,
                color: [55, 65, 81]
              });
              yPosition += itemHeight + 6;
            });
            yPosition += 10;
          }
        }

        // Trends section
        if (selectedSections.trends && industry.tabs?.trends) {
          const trends = industry.tabs.trends;
          checkPageBreak(50);

          doc.setFillColor(59, 130, 246);
          doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 24, 4, 4, 'F');

          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('MARKET TRENDS & INNOVATIONS', pageWidth / 2, yPosition + 15, { align: 'center' });

          yPosition += 34;

          if (trends.emergingTrends && Array.isArray(trends.emergingTrends)) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(59, 130, 246);
            doc.text('Emerging Trends', margin + 3, yPosition);
            yPosition += 12;

            trends.emergingTrends.forEach((item: any) => {
              doc.setFillColor(59, 130, 246);
              doc.circle(margin + 7, yPosition - 1.5, 1.5, 'F');

              const itemHeight = addText(String(item), margin + 12, yPosition, {
                maxWidth: 160,
                fontSize: 10,
                color: [55, 65, 81]
              });
              yPosition += itemHeight + 6;
            });
            yPosition += 10;
          }
        }

        // Incentives section
        if (selectedSections.incentives && industry.tabs?.incentives) {
          const incentives = industry.tabs.incentives;
          checkPageBreak(50);

          doc.setFillColor(34, 197, 94);
          doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 24, 4, 4, 'F');

          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('GOVERNMENT INCENTIVES & SUPPORT', pageWidth / 2, yPosition + 15, { align: 'center' });

          yPosition += 34;

          if (incentives.financial && Array.isArray(incentives.financial)) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(34, 197, 94);
            doc.text('Financial Incentives', margin + 3, yPosition);
            yPosition += 12;

            incentives.financial.forEach((item: any) => {
              doc.setFillColor(34, 197, 94);
              doc.circle(margin + 7, yPosition - 1.5, 1.5, 'F');

              const itemHeight = addText(String(item), margin + 12, yPosition, {
                maxWidth: 160,
                fontSize: 10,
                color: [55, 65, 81]
              });
              yPosition += itemHeight + 6;
            });
            yPosition += 10;
          }

          if (incentives.nonFinancial && Array.isArray(incentives.nonFinancial)) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(34, 197, 94);
            doc.text('Non-Financial Support', margin + 3, yPosition);
            yPosition += 12;

            incentives.nonFinancial.forEach((item: any) => {
              doc.setFillColor(34, 197, 94);
              doc.circle(margin + 7, yPosition - 1.5, 1.5, 'F');

              const itemHeight = addText(String(item), margin + 12, yPosition, {
                maxWidth: 160,
                fontSize: 10,
                color: [55, 65, 81]
              });
              yPosition += itemHeight + 6;
            });
          }
        }
      });

      // Sub-Industry Pages with modern design
      if (selectedSections.includeSubIndustries && selectedSubIndustriesData.length > 0) {
        selectedSubIndustriesData.forEach(subIndustry => {
          doc.addPage();
          yPosition = addHeader();

          // Modern sub-industry header
          doc.setFillColor(252, 248, 255);
          doc.rect(0, yPosition - 5, pageWidth, 45, 'F');

          // Accent bar on the left
          doc.setFillColor(147, 51, 234);
          doc.rect(0, yPosition - 5, 5, 45, 'F');

          // Badge for "Sub-Industry" with light purple
          doc.setFillColor(235, 220, 255);
          doc.roundedRect(margin + 5, yPosition + 2, 45, 10, 2, 2, 'F');

          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(147, 51, 234);
          doc.text('SUB-INDUSTRY', margin + 8, yPosition + 8);

          // Sub-industry name
          doc.setFontSize(22);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(37, 99, 235);
          if (subIndustry.name) {
            doc.text(subIndustry.name, margin + 5, yPosition + 25);
          }

          // Decorative underline
          doc.setDrawColor(147, 51, 234);
          doc.setLineWidth(2);
          doc.line(margin + 5, yPosition + 30, margin + 60, yPosition + 30);

          yPosition += 50;

          // Apply same sections as main industries for sub-industries
          if (selectedSections.description && subIndustry.description) {
            // Section title with gradient underline
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(37, 99, 235);
            doc.text('Overview', margin, yPosition);

            // Gradient underline
            doc.setDrawColor(37, 99, 235);
            doc.setLineWidth(2);
            doc.line(margin, yPosition + 2, margin + 35, yPosition + 2);

            yPosition += 14;

            // Content box with subtle background
            const contentHeight = Math.ceil(doc.splitTextToSize(subIndustry.description, 160).length * 4);
            doc.setFillColor(248, 250, 252);
            doc.roundedRect(margin, yPosition - 5, pageWidth - 2 * margin, contentHeight + 10, 3, 3, 'F');

            // Left accent bar
            doc.setFillColor(147, 51, 234);
            doc.roundedRect(margin, yPosition - 5, 3, contentHeight + 10, 2, 2, 'F');

            const descHeight = addText(subIndustry.description, margin + 8, yPosition, {
              maxWidth: 160,
              fontSize: 10,
              color: [55, 65, 81]
            });
            yPosition += descHeight + 15;
          }
        });
      }

      // Add VALUE CHAIN GLOBAL branded footer to all pages
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        // Footer gradient background
        doc.setFillColor(37, 99, 235);
        doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');

        // Purple gradient overlay
        doc.setFillColor(120, 70, 200);
        doc.rect(pageWidth * 0.7, pageHeight - 20, pageWidth * 0.3, 20, 'F');

        // Top decorative line
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(1);
        doc.line(0, pageHeight - 20, pageWidth, pageHeight - 20);

        // VALUE CHAIN GLOBAL branding - left
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text('VALUE CHAIN GLOBAL', margin, pageHeight - 10);

        // Tagline
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text('Industry Intelligence Platform', margin, pageHeight - 5);

        // Page number - center
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`${i} / ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });

        // Contact/Website - right
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('www.value-chain.global', pageWidth - margin, pageHeight - 10, { align: 'right' });

        doc.setFontSize(7);
        doc.text('© 2025 Vision 2030', pageWidth - margin, pageHeight - 5, { align: 'right' });
      }

      // Save the PDF
      const fileName = `Saudi_Industry_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      // Handle error appropriately
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      // Show print header, footer, and cover page
      const printHeader = document.querySelector('.print-header');
      const printFooter = document.querySelector('.print-footer');
      const coverPage = document.querySelector('.report-cover-page');

      if (printHeader) (printHeader as HTMLElement).style.display = 'flex';
      if (printFooter) (printFooter as HTMLElement).style.display = 'block';
      if (coverPage) (coverPage as HTMLElement).style.display = 'flex';

      // Get the preview panel element
      const element = document.querySelector('.printable-content');
      if (!element) {
        console.error('Preview content not found');
        return;
      }

      // Replace gradients with solid colors to prevent html2canvas errors
      const elementsToRestore: Array<{ element: HTMLElement; originalBackground: string; originalBackgroundImage: string }> = [];
      const allElements = element.querySelectorAll('*');

      allElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const computedStyle = window.getComputedStyle(htmlEl);
        const background = computedStyle.background || computedStyle.backgroundImage;

        if (background && (background.includes('linear-gradient') || background.includes('radial-gradient'))) {
          elementsToRestore.push({
            element: htmlEl,
            originalBackground: htmlEl.style.background || '',
            originalBackgroundImage: htmlEl.style.backgroundImage || ''
          });

          // Replace gradient with primary blue color
          htmlEl.style.setProperty('background', '#1976d2', 'important');
          htmlEl.style.setProperty('background-image', 'none', 'important');
        }
      });

      // Apply value chain table scaling for PDF
      const valueChainSections = document.querySelectorAll('.value-chain-section');
      valueChainSections.forEach(section => {
        (section as HTMLElement).style.transform = 'scale(0.55)';
        (section as HTMLElement).style.transformOrigin = 'top left';
        (section as HTMLElement).style.width = '182%';
      });

      // Wait for styles to apply and layout to settle
      await new Promise(resolve => setTimeout(resolve, 200));

      // Configure html2canvas for professional quality
      const canvas = await html2canvas(element as HTMLElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        windowWidth: Math.max(element.scrollWidth + 200, 1400),
        imageTimeout: 0,
        allowTaint: false,
        onclone: (clonedDoc) => {
          // Additional optimizations in cloned document
          const tables = clonedDoc.querySelectorAll('table');
          tables.forEach(table => {
            (table as HTMLElement).style.fontSize = '8px';
            (table as HTMLElement).style.lineHeight = '1.2';
          });

          // Ensure value chain sections are scaled in clone
          const clonedValueChains = clonedDoc.querySelectorAll('.value-chain-section');
          clonedValueChains.forEach(section => {
            (section as HTMLElement).style.transform = 'scale(0.55)';
            (section as HTMLElement).style.transformOrigin = 'top left';
            (section as HTMLElement).style.width = '182%';
          });
        }
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.95);

      // Create PDF with proper settings
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // Add additional pages
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      // Generate professional filename
      const industryNames = selectedIndustries
        .map(id => industries.find(i => i.id === id)?.name)
        .filter(Boolean)
        .join('_')
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_]/g, '')
        .substring(0, 50);

      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `VCG_Industry_Report_${industryNames || 'Report'}_${dateStr}.pdf`;

      // Save PDF
      pdf.save(filename);

      // Restore gradient styles
      elementsToRestore.forEach(({ element, originalBackground, originalBackgroundImage }) => {
        element.style.background = originalBackground;
        element.style.backgroundImage = originalBackgroundImage;
      });

      // Restore original styles
      valueChainSections.forEach(section => {
        (section as HTMLElement).style.transform = '';
        (section as HTMLElement).style.width = '';
      });

      // Hide print elements
      if (printHeader) (printHeader as HTMLElement).style.display = 'none';
      if (printFooter) (printFooter as HTMLElement).style.display = 'none';
      if (coverPage) (coverPage as HTMLElement).style.display = 'none';

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try using the browser print function (Ctrl/Cmd + P).');

      // Cleanup on error
      const valueChainSections = document.querySelectorAll('.value-chain-section');
      valueChainSections.forEach(section => {
        (section as HTMLElement).style.transform = '';
        (section as HTMLElement).style.width = '';
      });

      const printHeader = document.querySelector('.print-header');
      const printFooter = document.querySelector('.print-footer');
      const coverPage = document.querySelector('.report-cover-page');
      if (printHeader) (printHeader as HTMLElement).style.display = 'none';
      if (printFooter) (printFooter as HTMLElement).style.display = 'none';
      if (coverPage) (coverPage as HTMLElement).style.display = 'none';
    }
  };

  // Helper function to render Market Overview beautifully
  const renderMarketOverview = (data: any) => {
    if (!data) return null;
    if (typeof data === 'string') return <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{data}</Typography>;

    return (
      <Box>
        {/* Market Potential */}
        {data.marketPotential && data.marketPotential.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              Market Potential
            </Typography>
            <List sx={{ bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, p: 2 }}>
              {data.marketPotential.map((item: string, idx: number) => (
                <ListItem key={idx} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1', lineHeight: 1.6 }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Critical Gaps */}
        {data.criticalGaps && data.criticalGaps.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="error" gutterBottom>
              Critical Gaps
            </Typography>
            <List sx={{ bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 2, p: 2 }}>
              {data.criticalGaps.map((item: string, idx: number) => (
                <ListItem key={idx} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                    <WarningIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1', lineHeight: 1.6 }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Critical Strengths */}
        {data.criticalStrengths && data.criticalStrengths.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              Critical Strengths
            </Typography>
            <List sx={{ bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2, p: 2 }}>
              {data.criticalStrengths.map((item: string, idx: number) => (
                <ListItem key={idx} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                    <BusinessIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1', lineHeight: 1.6 }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    );
  };

  // Helper function to render Trends beautifully
  const renderTrends = (data: any) => {
    if (!data) return null;
    if (typeof data === 'string') return <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{data}</Typography>;

    return (
      <Box>
        {data.trendsList && data.trendsList.length > 0 && (
          <Grid container spacing={3}>
            {data.trendsList.map((trend: any, idx: number) => (
              <Grid item xs={12} md={6} key={idx}>
                <Card
                  elevation={3}
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                      p: 2.5,
                      borderBottom: `3px solid ${theme.palette.primary.main}`
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {trend.name}
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    {trend.points && trend.points.length > 0 && (
                      <Box>
                        {trend.points.map((point: string, pIdx: number) => (
                          <Box
                            key={pIdx}
                            sx={{
                              display: 'flex',
                              gap: 1.5,
                              mb: 2,
                              alignItems: 'flex-start'
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: theme.palette.primary.main,
                                mt: 1,
                                flexShrink: 0
                              }}
                            />
                            <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                              {point}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {data.whereToStart && (
          <Card
            elevation={2}
            sx={{
              mt: 4,
              borderRadius: 2,
              border: '2px solid',
              borderColor: theme.palette.success.main,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.light, 0.1)})`
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'success.main'
                  }}
                />
                Where to Start
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 2, color: 'text.primary' }}>
                {data.whereToStart}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  // Helper function to render Incentives beautifully
  const renderIncentives = (data: any) => {
    if (!data) return null;
    if (typeof data === 'string') return <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{data}</Typography>;

    return (
      <Box>
        {data.content && (
          <>
            {data.content.title && (
              <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                {data.content.title}
              </Typography>
            )}

            {data.content.sections && data.content.sections.map((section: any, sIdx: number) => (
              <Box key={sIdx} sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="secondary" gutterBottom sx={{ mt: 3 }}>
                  {section.title}
                </Typography>

                {section.items && section.items.map((item: any, iIdx: number) => (
                  <Paper key={iIdx} elevation={1} sx={{ p: 3, mb: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {item.title}
                    </Typography>

                    {item.subsections && item.subsections.map((subsection: any, subIdx: number) => (
                      <Box key={subIdx} sx={{ ml: 2, mb: 2, mt: 2 }}>
                        <Typography variant="body2" fontWeight="600" color="primary" gutterBottom>
                          {subsection.title}
                        </Typography>
                        {subsection.points && (
                          <List dense sx={{ pl: 2 }}>
                            {subsection.points.map((point: string, pIdx: number) => (
                              <ListItem key={pIdx} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                                <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                                  <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />
                                </ListItemIcon>
                                <ListItemText primary={point} primaryTypographyProps={{ variant: 'body2', lineHeight: 1.6 }} />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    ))}

                    {item.points && (
                      <List dense sx={{ mt: 1 }}>
                        {item.points.map((point: string, pIdx: number) => (
                          <ListItem key={pIdx} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                              <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary={point} primaryTypographyProps={{ variant: 'body2', lineHeight: 1.6 }} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                ))}
              </Box>
            ))}
          </>
        )}
      </Box>
    );
  };

  // Helper function to render Legal & Infrastructure
  const renderLegalInfrastructure = (data: any) => {
    if (!data) return null;
    if (typeof data === 'string') return <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{data}</Typography>;

    // If it's an object, render it nicely
    return (
      <Box>
        {Object.entries(data).map(([key, value]) => (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="secondary" gutterBottom sx={{ textTransform: 'capitalize' }}>
              {key.replace(/([A-Z])/g, ' $1').trim()}
            </Typography>
            {Array.isArray(value) ? (
              <List sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2, p: 2 }}>
                {value.map((item: any, idx: number) => (
                  <ListItem key={idx} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                      <CheckCircleIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={typeof item === 'object' ? JSON.stringify(item) : String(item)} primaryTypographyProps={{ variant: 'body1', lineHeight: 1.6 }} />
                  </ListItem>
                ))}
              </List>
            ) : typeof value === 'object' ? (
              <Paper elevation={1} sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.03) }}>
                {Object.entries(value as object).map(([subKey, subValue]) => (
                  <Typography key={subKey} variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                    <strong>{subKey.replace(/([A-Z])/g, ' $1').trim()}:</strong> {String(subValue)}
                  </Typography>
                ))}
              </Paper>
            ) : (
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{String(value)}</Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const sectionGroups = [
    {
      key: 'insights',
      title: 'Industry Insights',
      icon: <TrendingUpIcon />,
      color: theme.palette.primary.main,
      sections: [
        { key: 'summary', label: 'Executive Summary', description: 'High-level industry overview' },
        { key: 'description', label: 'Detailed Description', description: 'Comprehensive industry analysis' },
        { key: 'shortDescription', label: 'Short Description', description: 'Brief industry summary' },
        { key: 'growthRate', label: 'Growth Rate', description: 'Annual growth percentage' },
        { key: 'marketSize', label: 'Market Size', description: 'Total market valuation' },
        { key: 'investmentOpportunities', label: 'Investment Opportunities', description: 'Available investment options' },
        { key: 'keyMetrics', label: 'Key Performance Indicators', description: 'Employment, exports, companies' },
        { key: 'growthData', label: 'Historical Growth Data', description: 'Multi-year growth trends' },
        { key: 'tags', label: 'Industry Tags', description: 'Classification and keywords' }
      ]
    },
    {
      key: 'valueChain',
      title: 'Value Chain Analysis',
      icon: <AssessmentIcon />,
      color: theme.palette.secondary.main,
      sections: [
        { key: 'valueChainTable', label: 'Value Chain Table', description: 'Complete value chain overview table' }
      ]
    },
    {
      key: 'supportEcosystem',
      title: 'Support Ecosystem',
      icon: <BusinessIcon />,
      color: theme.palette.success.main,
      sections: [
        { key: 'supportEcosystem', label: 'Complete Support Ecosystem', description: 'Full ecosystem overview' },
        { key: 'supportEcosystemSummary', label: 'Ecosystem Summary', description: 'High-level ecosystem description' },
        { key: 'supportEcosystemTalent', label: 'Talent Development', description: 'Workforce and education programs' },
        { key: 'supportEcosystemFunding', label: 'Funding & Investment', description: 'Financial support mechanisms' },
        { key: 'supportEcosystemSupplyChain', label: 'Supply Chain', description: 'Logistics and supply networks' },
        { key: 'supportEcosystemPlayers', label: 'Support Organizations', description: 'Key supporting entities' }
      ]
    },
    {
      key: 'tabs',
      title: 'Deep Market Analysis',
      icon: <ScienceIcon />,
      color: theme.palette.info.main,
      sections: [
        { key: 'marketOverview', label: 'Market Overview', description: 'Comprehensive market analysis' },
        { key: 'legalInfrastructure', label: 'Legal & Infrastructure', description: 'Regulatory framework and infrastructure' },
        { key: 'trends', label: 'Market Trends', description: 'Current and future trends' },
        { key: 'incentives', label: 'Government Incentives', description: 'Available support programs' }
      ]
    }
  ];

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          /* Page Setup */
          @page {
            margin: 2cm 1.5cm;
            size: A4 portrait;
          }

          /* Force Light Theme - Override All Dark Mode Styles */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          html, body {
            background: white !important;
            color: #1a1a1a !important;
          }

          /* Hide sidebar and UI controls */
          .MuiGrid-container > .MuiGrid-item:first-child {
            display: none !important;
          }

          .MuiGrid-container > .MuiGrid-item:last-child {
            max-width: 100% !important;
            flex-basis: 100% !important;
          }

          .MuiContainer-root > .MuiPaper-root:first-child {
            display: none !important;
          }

          button, .MuiButton-root, .MuiFab-root, .no-print-button {
            display: none !important;
          }

          /* Container */
          .MuiContainer-root {
            padding: 0 !important;
            max-width: 100% !important;
          }

          /* Paper/Card - Light Background */
          .MuiPaper-root {
            background: white !important;
            color: #1a1a1a !important;
            box-shadow: none !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 8pt !important;
            margin-bottom: 16pt !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* All Box Elements */
          .MuiBox-root {
            background: transparent !important;
            color: #1a1a1a !important;
          }

          /* Typography - Force Dark Text */
          .MuiTypography-root,
          h1, h2, h3, h4, h5, h6, p, span, div, li {
            color: #1a1a1a !important;
          }

          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
            break-after: avoid;
          }

          /* Professional Print Header with VALUE CHAIN GLOBAL Branding */
          .print-header {
            display: block !important;
            background: linear-gradient(135deg, #2563eb 0%, #9333ea 100%) !important;
            padding: 24pt 20pt !important;
            margin: -2cm -1.5cm 20pt -1.5cm !important;
            position: relative;
            overflow: hidden;
          }

          .print-header::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: radial-gradient(circle at top right, rgba(255,255,255,0.15), transparent 70%);
          }

          .print-header * {
            color: white !important;
            position: relative;
            z-index: 1;
          }

          .print-header h1 {
            font-size: 32pt;
            margin: 0 0 8pt 0;
            font-weight: 700;
            letter-spacing: -0.5pt;
          }

          .print-header p {
            font-size: 13pt;
            margin: 0;
            opacity: 0.95;
          }

          /* Preserve gradient backgrounds in content */
          [style*="linear-gradient"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Tables - Light Theme with Gradient Headers */
          .MuiTable-root {
            background: white !important;
          }

          .MuiTableCell-root {
            background: white !important;
            color: #1a1a1a !important;
            border: 1px solid #d1d5db !important;
            padding: 8pt 12pt !important;
            font-size: 10pt !important;
          }

          .MuiTableCell-head {
            background: linear-gradient(135deg, #2563eb 0%, #9333ea 100%) !important;
            color: white !important;
            font-weight: 600 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .MuiTableRow-root {
            background: white !important;
          }

          .MuiTableRow-root:nth-child(even) .MuiTableCell-root {
            background: #f9fafb !important;
          }

          /* Chips - Light Theme */
          .MuiChip-root {
            background: white !important;
            border: 1px solid #2563eb !important;
            color: #2563eb !important;
            font-size: 9pt !important;
          }

          /* Dividers */
          .MuiDivider-root {
            border-color: #e5e7eb !important;
          }

          /* Grid Items */
          .MuiGrid-item {
            background: transparent !important;
          }

          /* Print Footer */
          .print-footer {
            display: block !important;
            text-align: center;
            margin: 20pt -1.5cm -2cm -1.5cm !important;
            padding: 16pt 20pt !important;
            border-top: 3pt solid #2563eb;
            background: white !important;
            font-size: 10pt;
            color: #374151 !important;
          }

          /* Override Any Dark Backgrounds */
          [style*="background-color"]:not([style*="linear-gradient"]) {
            background-color: white !important;
          }

          /* Keep Gradients */
          [style*="linear-gradient"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Value Chain Headers with Gradient */
          .MuiPaper-root > .MuiBox-root[style*="linear-gradient"] {
            background: linear-gradient(135deg, #2563eb 0%, #9333ea 100%) !important;
            border-radius: 8pt 8pt 0 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .MuiPaper-root > .MuiBox-root[style*="linear-gradient"] * {
            color: white !important;
          }

          /* FIX: Value Chain Table Width - Critical scaling fix */
          .value-chain-section {
            transform: scale(0.55) !important;
            transform-origin: top left !important;
            width: 182% !important; /* 100 / 0.55 */
            margin-bottom: 40pt !important;
            overflow: visible !important;
          }

          /* Value chain table text sizing */
          .value-chain-section table,
          .value-chain-section .MuiTypography-root {
            font-size: 7pt !important;
            line-height: 1.2 !important;
          }

          .value-chain-section .MuiTableCell-root {
            padding: 4pt 3pt !important;
            white-space: normal !important;
            word-wrap: break-word !important;
          }

          .value-chain-section .MuiChip-root {
            font-size: 6pt !important;
            height: 14px !important;
            padding: 1px 4px !important;
          }

          /* Value chain header */
          .value-chain-section > div > div:first-child {
            font-size: 12pt !important;
          }

          /* Professional Report Cover Page */
          .report-cover-page {
            display: block !important;
            page-break-after: always;
            height: 100vh;
            position: relative;
            background: white !important;
          }

          /* Professional Report Header */
          .report-header {
            display: flex !important;
            justify-content: space-between;
            align-items: center;
            padding: 20pt;
            border-bottom: 2pt solid #2563eb;
            margin-bottom: 20pt;
          }

          .report-header img {
            max-height: 50px;
            width: auto;
          }

          .report-metadata {
            text-align: right;
            font-size: 9pt;
            color: #374151;
          }

          /* Page Numbers */
          @page {
            @bottom-right {
              content: "Page " counter(page) " of " counter(pages);
              font-size: 9pt;
              color: #374151;
            }
          }

          /* Professional Footer on each page */
          .report-page-footer {
            display: block !important;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 12pt 20pt;
            border-top: 1pt solid #e5e7eb;
            background: white;
            font-size: 8pt;
            color: #6b7280;
            text-align: center;
          }

          /* Section Title Styling */
          .section-title {
            color: #2563eb !important;
            font-weight: 700 !important;
            font-size: 16pt !important;
            margin-top: 20pt !important;
            margin-bottom: 12pt !important;
            padding-bottom: 6pt !important;
            border-bottom: 2pt solid #2563eb !important;
          }

          /* Metric Cards in Print */
          .metric-card {
            background: #f9fafb !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 6pt !important;
            padding: 12pt !important;
            margin-bottom: 10pt !important;
          }

          .metric-value {
            font-size: 20pt !important;
            font-weight: 700 !important;
            color: #2563eb !important;
          }

          .metric-label {
            font-size: 9pt !important;
            color: #6b7280 !important;
            text-transform: uppercase;
            letter-spacing: 0.5pt;
          }
        }

        /* Hide print header/footer by default */
        .print-header, .print-footer {
          display: none;
        }

        @media screen {
          .no-print-button {
            display: inline-flex;
          }
        }

        @media print {
          .no-print-button {
            display: none !important;
          }
        }
      `}</style>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Hero Section */}
        <MotionPaper
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.secondary.main, 0.9)} 100%)`,
          color: 'white',
          p: { xs: 4, md: 6 },
          borderRadius: 3,
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`
        }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          {/* <Avatar
            src={logoLight}
            sx={{ width: 48, height: 48, mr: 2 }}
          /> */}
          <Typography variant="h3" component="h1" fontWeight="bold">
            Industry Report Generator
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Generate comprehensive industry analysis reports for Saudi Vision 2030
        </Typography>
      </MotionPaper>

      <Grid container spacing={4}>
        {/* Configuration Panel */}
        <Grid item xs={12} md={4}>
          <MotionCard
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            sx={{
              height: 'fit-content',
              position: 'sticky',
              top: 20,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <SettingsIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Report Configuration
                </Typography>
              </Box>

              {/* Industry Selection */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                  Select Industries
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Choose Industries</InputLabel>
                  <Select
                    multiple
                    value={selectedIndustries}
                    onChange={handleIndustryChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const industry = industries.find(ind => ind.id === value);
                          return (
                            <Chip
                              key={value}
                              label={industry?.name || value}
                              size="small"
                              sx={{
                                bgcolor: getCategoryColor(industry?.category || ''),
                                color: 'white',
                                '& .MuiChip-deleteIcon': { color: 'white' }
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {categories.map((category) => [
                      <MenuItem key={category} disabled sx={{ 
                        fontWeight: 'bold', 
                        color: getCategoryColor(category),
                        backgroundColor: alpha(getCategoryColor(category), 0.1)
                      }}>
                        {getCategoryIcon(category)}
                        <Box ml={1}>{category}</Box>
                      </MenuItem>,
                      ...industries
                        .filter(industry => industry.category === category)
                        .map((industry) => (
                          <MenuItem key={industry.id} value={industry.id}>
                            <Box ml={3}>{industry.name}</Box>
                          </MenuItem>
                        ))
                    ])}
                  </Select>
                </FormControl>
              </Box>

              {/* Sub-Industry Selection */}
              {availableSubIndustries.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                    Select Sub-Industries
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Choose Sub-Industries</InputLabel>
                    <Select
                      multiple
                      value={selectedSubIndustries}
                      onChange={handleSubIndustryChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const subIndustry = availableSubIndustries.find(sub => sub.id === value);
                            return (
                              <Chip
                                key={value}
                                label={subIndustry?.name || value}
                                size="small"
                                color="secondary"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {availableSubIndustries.map((subIndustry) => (
                        <MenuItem key={subIndustry.id} value={subIndustry.id}>
                          {subIndustry.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Report Sections */}
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                Select Report Sections
              </Typography>

              {sectionGroups.map((group) => (
                <Box key={group.key} mb={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: alpha(group.color, 0.05)
                      }
                    }}
                    onClick={() => toggleSectionExpansion(group.key as keyof typeof expandedSections)}
                  >
                    <Box display="flex" alignItems="center">
                      <Box sx={{ color: group.color, mr: 1 }}>
                        {group.icon}
                      </Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {group.title}
                      </Typography>
                    </Box>
                    {expandedSections[group.key as keyof typeof expandedSections] ? 
                      <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                  
                  <Collapse in={expandedSections[group.key as keyof typeof expandedSections]}>
                    <Box ml={4} mt={1}>
                      {group.sections.map((section) => (
                        <StyledFormControlLabel
                          key={section.key}
                          control={
                            <Checkbox
                              checked={selectedSections[section.key as keyof typeof selectedSections]}
                              onChange={handleSectionChange(section.key as keyof typeof selectedSections)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {section.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {section.description}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              ))}

              {/* Sub-Industries Toggle */}
              {availableSubIndustries.length > 0 && (
                <Box mt={2}>
                  <StyledFormControlLabel
                    control={
                      <Checkbox
                        checked={selectedSections.includeSubIndustries}
                        onChange={handleSectionChange('includeSubIndustries')}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Include Sub-Industries
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Add detailed sub-industry analysis
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Generate Buttons */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleDownloadPDF}
                  disabled={!isValidForGeneration()}
                  startIcon={<DownloadIcon />}
                  sx={{
                    py: 1.5,
                    fontWeight: 'bold',
                    background: isValidForGeneration() ?
                      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` :
                      undefined
                  }}
                >
                  Download PDF
                </Button>

              </Box>

              {!isValidForGeneration() && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <AlertTitle>Configuration Required</AlertTitle>
                  Please select at least one industry and one report section to generate a report.
                </Alert>
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Preview Panel */}
        <Grid item xs={12} md={8}>
          <MotionCard
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <CardContent>
              <SectionHeader>
                <Typography variant="h5" fontWeight="bold">
                  Report Preview
                </Typography>
              </SectionHeader>

              {selectedIndustries.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" mb={1}>
                    No Industries Selected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select industries from the configuration panel to preview your report
                  </Typography>
                </Box>
              ) : (
                <Box className="printable-content">
                  {/* Professional Report Header - Only visible when printing */}
                  <Box className="print-header report-header" sx={{ mb: 4, display: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={logoLight}
                        variant="square"
                        sx={{ width: 120, height: 'auto', bgcolor: 'transparent' }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a', lineHeight: 1.2 }}>
                          VALUE CHAIN GLOBAL
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                          Industry Intelligence & Analysis Platform
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          Saudi Vision 2030
                        </Typography>
                      </Box>
                    </Box>
                    <Box className="report-metadata">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
                        {getSelectedIndustriesData()[0]?.name || 'Industry Report'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: '#6b7280' }}>
                        Report Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: '#6b7280' }}>
                        Generated: {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: '#2563eb', fontWeight: 600, mt: 0.5 }}>
                        www.value-chain.global
                      </Typography>
                    </Box>
                  </Box>

                  {/* Professional Footer - Only visible when printing */}
                  <Box className="print-footer report-page-footer" sx={{ display: 'none' }}>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      © {new Date().getFullYear()} Value Chain Global | Industry Intelligence & Analysis Platform | www.value-chain.global
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#9ca3af', mt: 0.5, fontSize: '7pt' }}>
                      This report is confidential and proprietary. Distribution without authorization is prohibited.
                    </Typography>
                  </Box>

                  {/* Professional Cover Page - McKinsey/BCG Quality */}
                  <Box
                    className="report-cover-page"
                    sx={{
                      display: 'none',
                      '@media print': {
                        display: 'flex !important',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minHeight: '100vh',
                        padding: 6,
                        background: 'linear-gradient(135deg, #00A7E1 0%, #00D4AA 50%, #A4E63C 100%)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden'
                      }
                    }}
                  >
                    {/* Header with Logo */}
                    <Box>
                      <Avatar
                        src={logoLight}
                        variant="square"
                        sx={{
                          height: 60,
                          width: 'auto',
                          mb: 3,
                          bgcolor: 'transparent',
                          filter: 'brightness(0) invert(1)'
                        }}
                      />
                      <Typography
                        variant="overline"
                        sx={{
                          fontSize: 14,
                          letterSpacing: 2,
                          fontWeight: 600,
                          display: 'block',
                          mb: 1
                        }}
                      >
                        INDUSTRY INTELLIGENCE REPORT
                      </Typography>
                    </Box>

                    {/* Main Title Section */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: '70%' }}>
                      <Typography
                        variant="h1"
                        sx={{
                          fontSize: 72,
                          fontWeight: 800,
                          lineHeight: 1.1,
                          mb: 3,
                          textShadow: '0 2px 20px rgba(0,0,0,0.2)'
                        }}
                      >
                        {getSelectedIndustriesData()[0]?.name || 'Industry Report'}
                      </Typography>

                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: 28,
                          fontWeight: 300,
                          opacity: 0.95,
                          mb: 6,
                          lineHeight: 1.4
                        }}
                      >
                        Comprehensive Market Analysis & Strategic Insights
                      </Typography>

                      {/* Key Metrics on Cover */}
                      {getSelectedIndustriesData()[0] && (
                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {getSelectedIndustriesData()[0].shortDescription && (
                            <Box>
                              <Typography variant="h3" fontWeight={700}>
                                {getSelectedIndustriesData()[0].shortDescription}
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                Investment Scale
                              </Typography>
                            </Box>
                          )}
                          {getSelectedIndustriesData()[0].marketSize && (
                            <Box>
                              <Typography variant="h3" fontWeight={700}>
                                ${getSelectedIndustriesData()[0].marketSize}
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                Market Size
                              </Typography>
                            </Box>
                          )}
                          {getSelectedIndustriesData()[0].growthRate && (
                            <Box>
                              <Typography variant="h3" fontWeight={700}>
                                {getSelectedIndustriesData()[0].growthRate}%
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                Annual Growth
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Footer */}
                    <Box>
                      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', mb: 3 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>
                            Report Date
                          </Typography>
                          <Typography variant="body1">
                            {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>
                            Generated At
                          </Typography>
                          <Typography variant="body1">
                            {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>
                            Platform
                          </Typography>
                          <Typography variant="body1">
                            VALUE CHAIN GLOBAL • Industry Intelligence & Analysis Platform
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                            Saudi Vision 2030 Initiative • www.value-chain.global
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>

                  {/* Table of Contents Page - Only visible when printing */}
                  <Box
                    className="table-of-contents-page"
                    sx={{
                      display: 'none',
                      '@media print': {
                        display: 'flex !important',
                        flexDirection: 'column',
                        minHeight: '100vh',
                        p: 6,
                        bgcolor: '#FAFAFA',
                        pageBreakAfter: 'always'
                      }
                    }}
                  >
                    {/* Mini Header */}
                    <Box sx={{ mb: 6 }}>
                      <Avatar
                        src={logoLight}
                        variant="square"
                        sx={{ height: 40, width: 'auto', mb: 2, bgcolor: 'transparent' }}
                      />
                      <Typography variant="h3" fontWeight={700} color="primary.main">
                        Table of Contents
                      </Typography>
                    </Box>

                    {/* Sections List */}
                    <Box sx={{ flex: 1 }}>
                      {[
                        { title: 'Executive Summary', description: 'High-level industry overview', page: 3 },
                        selectedSections.description && { title: 'Industry Description', description: 'Detailed sector information', page: 4 },
                        (selectedSections.growthRate || selectedSections.marketSize) && { title: 'Market Overview', description: 'Growth rate and market size analysis', page: 5 },
                        selectedSections.keyMetrics && { title: 'Key Performance Indicators', description: 'Critical industry metrics', page: 6 },
                        selectedSections.growthData && { title: 'Historical Growth Data', description: 'Year-over-year trends', page: 7 },
                        selectedSections.marketOverview && { title: 'Market Analysis', description: 'Comprehensive market insights', page: 8 },
                        selectedSections.trends && { title: 'Market Trends', description: 'Emerging industry patterns', page: 9 },
                        selectedSections.valueChainTable && { title: 'Value Chain Analysis', description: 'Complete value chain mapping', page: 10 },
                        selectedSections.supportEcosystem && { title: 'Support Ecosystem', description: 'Talent, funding, and infrastructure', page: 11 },
                        selectedSections.legalInfrastructure && { title: 'Legal Infrastructure', description: 'Regulatory framework', page: 12 },
                        selectedSections.incentives && { title: 'Government Incentives', description: 'Available support programs', page: 13 }
                      ].filter((item): item is { title: string; description: string; page: number } => Boolean(item)).map((section, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 2,
                            borderBottom: '1px solid #E0E0E0',
                            '&:hover': { bgcolor: '#F5F5F5' }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 700,
                                minWidth: 40
                              }}
                            >
                              {(index + 1).toString().padStart(2, '0')}
                            </Typography>
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {section.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {section.description}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="h6" color="text.secondary">
                            {section.page}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Footer */}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 4 }}>
                      Report generated by VALUE CHAIN GLOBAL • {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>

                  {/* Detailed Industry Data - Visible on Screen and Print */}
                  <Box sx={{ mt: 2 }}>
                    {getSelectedIndustriesData().map((industry, index) => (
                      <Box key={industry.id} sx={{ mb: 6, pageBreakAfter: index < getSelectedIndustriesData().length - 1 ? 'always' : 'auto' }}>
                        {/* Industry Header */}
                        <Paper
                          elevation={2}
                          sx={{
                            p: 4,
                            mb: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.secondary.main, 0.9)} 100%)`,
                            color: 'white'
                          }}
                        >
                          <Typography variant="h4" fontWeight="bold" gutterBottom>
                            {industry.name}
                          </Typography>
                          {industry.description && (
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                              {industry.description}
                            </Typography>
                          )}
                          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {industry.category && (
                              <Chip label={industry.category} sx={{ bgcolor: 'white', color: theme.palette.primary.main }} />
                            )}
                            {industry.marketSize && (
                              <Chip label={`Market: $${industry.marketSize}`} sx={{ bgcolor: 'white', color: theme.palette.primary.main }} />
                            )}
                            {industry.growthRate !== undefined && (
                              <Chip label={`Growth: ${industry.growthRate}%`} sx={{ bgcolor: 'white', color: theme.palette.primary.main }} />
                            )}
                          </Box>
                        </Paper>

                        {/* Summary Section */}
                        {selectedSections.summary && industry.summary && (
                          <Paper
                            elevation={0}
                            className="section-block"
                            sx={{
                              p: 4,
                              mb: 4,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.02)
                            }}
                          >
                            <Typography
                              variant="h5"
                              className="section-title"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                mb: 3,
                                pb: 1.5,
                                borderBottom: `3px solid ${theme.palette.primary.main}`,
                                display: 'inline-block'
                              }}
                            >
                              Executive Summary
                            </Typography>
                            <Box
                              sx={{
                                p: 3,
                                bgcolor: 'white',
                                borderRadius: 2,
                                borderLeft: `4px solid ${theme.palette.primary.main}`
                              }}
                            >
                              <Typography variant="body1" sx={{ lineHeight: 1.9, color: '#374151', fontSize: '1.05rem' }}>
                                {industry.summary}
                              </Typography>
                            </Box>
                          </Paper>
                        )}

                        {/* Description */}
                        {selectedSections.description && industry.description && (
                          <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.secondary.main}` }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                              Industry Description
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{industry.description}</Typography>
                          </Paper>
                        )}

                        {/* Market Overview - Professional Cards */}
                        {(selectedSections.growthRate || selectedSections.marketSize) && (industry.growthRate !== undefined || industry.marketSize) && (
                          <Box sx={{ mb: 4 }}>
                            <Typography
                              variant="h5"
                              className="section-title"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                mb: 3,
                                pb: 1.5,
                                borderBottom: `3px solid ${theme.palette.primary.main}`,
                                display: 'inline-block'
                              }}
                            >
                              Market Overview
                            </Typography>
                            <Grid container spacing={3} sx={{ mt: 1 }}>
                              {selectedSections.growthRate && industry.growthRate !== undefined && (
                                <Grid item xs={12} sm={6}>
                                  <Card
                                    elevation={2}
                                    sx={{
                                      height: '100%',
                                      background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                                      border: '2px solid #81C784'
                                    }}
                                  >
                                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                      <Typography variant="caption" fontWeight={600} color="success.dark" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                        ANNUAL GROWTH RATE
                                      </Typography>
                                      <Typography variant="h2" fontWeight={800} color="success.dark" sx={{ my: 2 }}>
                                        {industry.growthRate}%
                                      </Typography>
                                      <TrendingUpIcon sx={{ color: 'success.dark', fontSize: 32 }} />
                                    </CardContent>
                                  </Card>
                                </Grid>
                              )}
                              {selectedSections.marketSize && industry.marketSize && (
                                <Grid item xs={12} sm={6}>
                                  <Card
                                    elevation={2}
                                    sx={{
                                      height: '100%',
                                      background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                                      border: '2px solid #64B5F6'
                                    }}
                                  >
                                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                      <Typography variant="caption" fontWeight={600} color="primary.dark" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                        MARKET SIZE
                                      </Typography>
                                      <Typography variant="h2" fontWeight={800} color="primary.dark" sx={{ my: 2 }}>
                                        ${industry.marketSize}
                                      </Typography>
                                      <BusinessIcon sx={{ color: 'primary.dark', fontSize: 32 }} />
                                    </CardContent>
                                  </Card>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}

                        {/* Key Metrics */}
                        {selectedSections.keyMetrics && industry.keyMetrics && Object.keys(industry.keyMetrics).length > 0 && (
                          <Paper
                            elevation={0}
                            className="section-block"
                            sx={{
                              p: 4,
                              mb: 4,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.02)
                            }}
                          >
                            <Typography
                              variant="h5"
                              className="section-title"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                mb: 3,
                                pb: 1.5,
                                borderBottom: `3px solid ${theme.palette.primary.main}`,
                                display: 'inline-block'
                              }}
                            >
                              Key Performance Indicators
                            </Typography>
                            <Grid container spacing={3} sx={{ mt: 1 }}>
                              {Object.entries(industry.keyMetrics).map(([key, value]) => (
                                <Grid item xs={12} sm={6} md={4} key={key}>
                                  <Box
                                    className="metric-card"
                                    sx={{
                                      p: 3,
                                      bgcolor: 'white',
                                      borderRadius: 2,
                                      border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                      textAlign: 'center',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                      }
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      className="metric-label"
                                      sx={{
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: 1,
                                        fontWeight: 600,
                                        display: 'block',
                                        mb: 1.5
                                      }}
                                    >
                                      {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      className="metric-value"
                                      sx={{
                                        fontWeight: 700,
                                        color: theme.palette.primary.main,
                                        lineHeight: 1
                                      }}
                                    >
                                      {value}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                        )}

                        {/* Growth Data - Visual Timeline */}
                        {selectedSections.growthData && industry.growthData && industry.growthData.length > 0 && (
                          <Box sx={{ mb: 4 }}>
                            <Typography
                              variant="h5"
                              className="section-title"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                mb: 4,
                                pb: 1.5,
                                borderBottom: `3px solid ${theme.palette.primary.main}`
                              }}
                            >
                              Historical Growth Data
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 3,
                                position: 'relative',
                                px: 2,
                                py: 4,
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: '50px',
                                  left: '40px',
                                  right: '40px',
                                  height: 3,
                                  background: 'linear-gradient(90deg, #00A7E1 0%, #00D4AA 50%, #A4E63C 100%)',
                                  zIndex: 0
                                }
                              }}
                            >
                              {industry.growthData.map((data, index) => (
                                <Box
                                  key={data.year}
                                  sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    position: 'relative',
                                    zIndex: 1
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 80,
                                      height: 80,
                                      borderRadius: '50%',
                                      bgcolor: 'white',
                                      border: '4px solid',
                                      borderColor: index === (industry.growthData?.length || 0) - 1 ? '#A4E63C' : '#00A7E1',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                      }
                                    }}
                                  >
                                    <Typography
                                      variant="h5"
                                      fontWeight={700}
                                      sx={{
                                        color: index === (industry.growthData?.length || 0) - 1 ? '#A4E63C' : '#00A7E1'
                                      }}
                                    >
                                      {data.value}%
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight={600}
                                    sx={{
                                      mt: 2,
                                      color: 'text.primary',
                                      letterSpacing: 0.5
                                    }}
                                  >
                                    {data.year}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Tags */}
                        {selectedSections.tags && industry.tags && industry.tags.length > 0 && (
                          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                              Industry Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {industry.tags.map((tag, i) => (
                                <Chip key={i} label={tag} color="primary" variant="outlined" />
                              ))}
                            </Box>
                          </Paper>
                        )}

                        {/* Short Description */}
                        {selectedSections.shortDescription && industry.shortDescription && (
                          <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.info.main}` }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom color="info.main">
                              Short Description
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{industry.shortDescription}</Typography>
                          </Paper>
                        )}

                        {/* Tabs Data - Market Overview, Legal, Trends, Incentives - BEFORE Value Chain */}
                        {industry.tabs && (
                          <>
                            {selectedSections.marketOverview && industry.tabs.marketOverview && (
                              <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.info.main}` }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom color="info.main">
                                  Market Overview
                                </Typography>
                                {renderMarketOverview(industry.tabs.marketOverview)}
                              </Paper>
                            )}

                            {selectedSections.legalInfrastructure && industry.tabs.legalInfrastructure && (
                              <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.secondary.main}` }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                                  Legal & Infrastructure
                                </Typography>
                                {renderLegalInfrastructure(industry.tabs.legalInfrastructure)}
                              </Paper>
                            )}

                            {selectedSections.trends && industry.tabs.trends && (
                              <Box sx={{ mb: 4 }}>
                                <Typography
                                  variant="h5"
                                  className="section-title"
                                  sx={{
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    mb: 4,
                                    pb: 1.5,
                                    borderBottom: `3px solid ${theme.palette.primary.main}`
                                  }}
                                >
                                  Market Trends
                                </Typography>
                                {renderTrends(industry.tabs.trends)}
                              </Box>
                            )}

                            {selectedSections.incentives && industry.tabs.incentives && (
                              <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.warning.main}` }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom color="warning.main">
                                  Government Incentives
                                </Typography>
                                {renderIncentives(industry.tabs.incentives)}
                              </Paper>
                            )}
                          </>
                        )}

                        {/* Value Chain Table */}
                        {selectedSections.valueChainTable && (
                          <Paper
                            elevation={2}
                            className="value-chain-section"
                            sx={{
                              mt: 4,
                              mb: 3,
                              borderRadius: 2,
                              overflow: 'visible',
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              '@media print': {
                                pageBreakInside: 'avoid',
                                overflow: 'visible'
                              }
                            }}
                          >
                            <Box
                              sx={{
                                p: 3,
                                background: `linear-gradient(90deg, #00A7E1 0%, #00D4AA 50%, #A4E63C 100%)`,
                                color: 'white',
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                            >
                              <Typography variant="h5" fontWeight={700} sx={{ position: 'relative', zIndex: 1 }}>
                                Value Chain Analysis
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5, position: 'relative', zIndex: 1 }}>
                                Complete industry value chain mapping across all stages
                              </Typography>
                            </Box>
                            <Box p={2}>
                              <ValueChainTable
                                valueChainId={industry.name === 'Pharmaceutical Sector' ? 'pharma' : industry.id}
                                valueChainData={industry.valueChain && industry.valueChain.stages ? industry.valueChain : null}
                                industryName={industry.name}
                                industryId={industry.id}
                                subIndustryId={undefined}
                                subIndustries={[]}
                                onNavigate={() => {}}
                              />
                            </Box>
                          </Paper>
                        )}

                        {/* Support Ecosystem */}
                        {(selectedSections.supportEcosystem || selectedSections.supportEcosystemSummary || selectedSections.supportEcosystemTalent || selectedSections.supportEcosystemFunding || selectedSections.supportEcosystemSupplyChain || selectedSections.supportEcosystemPlayers) && industry.supportEcosystemRecord && (
                          <Box sx={{ mb: 4 }}>
                            <Typography
                              variant="h5"
                              className="section-title"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                mb: 4,
                                pb: 1.5,
                                borderBottom: `3px solid ${theme.palette.primary.main}`
                              }}
                            >
                              Support Ecosystem
                            </Typography>

                            {(selectedSections.supportEcosystem || selectedSections.supportEcosystemSummary) && industry.supportEcosystemRecord.summary && (
                              <Card
                                elevation={2}
                                sx={{
                                  mb: 3,
                                  borderRadius: 2,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                                  border: '1px solid',
                                  borderColor: alpha(theme.palette.primary.main, 0.2)
                                }}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.primary' }}>
                                    {industry.supportEcosystemRecord.summary}
                                  </Typography>
                                </CardContent>
                              </Card>
                            )}

                            <Grid container spacing={3}>
                              {/* Talent */}
                              {(selectedSections.supportEcosystem || selectedSections.supportEcosystemTalent) && industry.supportEcosystemRecord.talent && Object.keys(industry.supportEcosystemRecord.talent).length > 0 && (
                                <Grid item xs={12} md={4}>
                                  <Card
                                    elevation={3}
                                    sx={{
                                      height: '100%',
                                      borderRadius: 2,
                                      border: '2px solid',
                                      borderColor: theme.palette.success.main,
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                                        p: 2.5,
                                        color: 'white'
                                      }}
                                    >
                                      <Typography variant="h6" fontWeight={700}>
                                        Talent Development
                                      </Typography>
                                    </Box>
                                    <CardContent sx={{ p: 2.5 }}>
                                      {Object.entries(industry.supportEcosystemRecord.talent).map(([key, value]) => (
                                        <Box key={key} sx={{ mb: 2 }}>
                                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                          </Typography>
                                          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
                                            {String(value)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              )}

                              {/* Funding */}
                              {(selectedSections.supportEcosystem || selectedSections.supportEcosystemFunding) && industry.supportEcosystemRecord.funding && Object.keys(industry.supportEcosystemRecord.funding).length > 0 && (
                                <Grid item xs={12} md={4}>
                                  <Card
                                    elevation={3}
                                    sx={{
                                      height: '100%',
                                      borderRadius: 2,
                                      border: '2px solid',
                                      borderColor: theme.palette.info.main,
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.light})`,
                                        p: 2.5,
                                        color: 'white'
                                      }}
                                    >
                                      <Typography variant="h6" fontWeight={700}>
                                        Funding & Investment
                                      </Typography>
                                    </Box>
                                    <CardContent sx={{ p: 2.5 }}>
                                      {Object.entries(industry.supportEcosystemRecord.funding).map(([key, value]) => (
                                        <Box key={key} sx={{ mb: 2 }}>
                                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                          </Typography>
                                          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
                                            {String(value)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              )}

                              {/* Supply Chain */}
                              {(selectedSections.supportEcosystem || selectedSections.supportEcosystemSupplyChain) && industry.supportEcosystemRecord.supplyChain && Object.keys(industry.supportEcosystemRecord.supplyChain).length > 0 && (
                                <Grid item xs={12} md={4}>
                                  <Card
                                    elevation={3}
                                    sx={{
                                      height: '100%',
                                      borderRadius: 2,
                                      border: '2px solid',
                                      borderColor: theme.palette.warning.main,
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                                      }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
                                        p: 2.5,
                                        color: 'white'
                                      }}
                                    >
                                      <Typography variant="h6" fontWeight={700}>
                                        Supply Chain
                                      </Typography>
                                    </Box>
                                    <CardContent sx={{ p: 2.5 }}>
                                      {Object.entries(industry.supportEcosystemRecord.supplyChain).map(([key, value]) => (
                                        <Box key={key} sx={{ mb: 2 }}>
                                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                          </Typography>
                                          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
                                            {String(value)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              )}
                            </Grid>

                            {(selectedSections.supportEcosystem || selectedSections.supportEcosystemPlayers) && industry.supportEcosystemRecord.keyPlayers && industry.supportEcosystemRecord.keyPlayers.length > 0 && (
                              <Card
                                elevation={2}
                                sx={{
                                  mt: 3,
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: alpha(theme.palette.primary.main, 0.2)
                                }}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom>
                                    Key Support Organizations
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
                                    {industry.supportEcosystemRecord.keyPlayers.map((player, i) => (
                                      <Chip
                                        key={i}
                                        label={player}
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: '0.85rem',
                                          '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                                          }
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </CardContent>
                              </Card>
                            )}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>

                  {/* Sub-Industries Data */}
                  {selectedSections.includeSubIndustries && getSelectedSubIndustriesData().length > 0 && (
                    <Box sx={{ mt: 6 }}>
                      <Divider sx={{ mb: 4 }}>
                        <Chip label="SUB-INDUSTRIES" color="secondary" />
                      </Divider>

                      {getSelectedSubIndustriesData().map((subIndustry, subIndex) => (
                        <Box key={subIndustry.id} sx={{ mb: 6, pageBreakAfter: subIndex < getSelectedSubIndustriesData().length - 1 ? 'always' : 'auto' }}>
                          {/* Sub-Industry Header */}
                          <Paper
                            elevation={2}
                            sx={{
                              p: 4,
                              mb: 3,
                              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%)`,
                              color: 'white'
                            }}
                          >
                            <Chip label="SUB-INDUSTRY" size="small" sx={{ bgcolor: 'white', color: theme.palette.secondary.main, mb: 2 }} />
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                              {subIndustry.name}
                            </Typography>
                            {subIndustry.description && (
                              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                {subIndustry.description}
                              </Typography>
                            )}
                            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              {subIndustry.marketSize && (
                                <Chip label={`Market: $${subIndustry.marketSize}`} sx={{ bgcolor: 'white', color: theme.palette.secondary.main }} />
                              )}
                              {subIndustry.growthRate !== undefined && (
                                <Chip label={`Growth: ${subIndustry.growthRate}%`} sx={{ bgcolor: 'white', color: theme.palette.secondary.main }} />
                              )}
                            </Box>
                          </Paper>

                          {/* Sub-Industry Summary */}
                          {selectedSections.summary && subIndustry.summary && (
                            <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.secondary.main}` }}>
                              <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                                Executive Summary
                              </Typography>
                              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{subIndustry.summary}</Typography>
                            </Paper>
                          )}

                          {/* Sub-Industry Description */}
                          {selectedSections.description && subIndustry.description && (
                            <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.info.main}` }}>
                              <Typography variant="h6" fontWeight="bold" gutterBottom color="info.main">
                                Description
                              </Typography>
                              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{subIndustry.description}</Typography>
                            </Paper>
                          )}

                          {/* Sub-Industry Growth & Market */}
                          {(selectedSections.growthRate || selectedSections.marketSize) && (subIndustry.growthRate !== undefined || subIndustry.marketSize) && (
                            <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                              <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                                Market Overview
                              </Typography>
                              <Grid container spacing={3}>
                                {selectedSections.growthRate && subIndustry.growthRate !== undefined && (
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, textAlign: 'center' }}>
                                      <Typography variant="body2" color="text.secondary" gutterBottom>Annual Growth Rate</Typography>
                                      <Typography variant="h4" fontWeight="bold" color="success.main">{subIndustry.growthRate}%</Typography>
                                    </Box>
                                  </Grid>
                                )}
                                {selectedSections.marketSize && subIndustry.marketSize && (
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, textAlign: 'center' }}>
                                      <Typography variant="body2" color="text.secondary" gutterBottom>Market Size</Typography>
                                      <Typography variant="h4" fontWeight="bold" color="secondary.main">${subIndustry.marketSize}</Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          )}

                          {/* Sub-Industry Key Metrics */}
                          {selectedSections.keyMetrics && subIndustry.keyMetrics && Object.keys(subIndustry.keyMetrics).length > 0 && (
                            <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                              <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                                Key Performance Indicators
                              </Typography>
                              <Grid container spacing={2}>
                                {Object.entries(subIndustry.keyMetrics).map(([key, value]) => (
                                  <Grid item xs={6} sm={4} key={key}>
                                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}` }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                      </Typography>
                                      <Typography variant="h6" fontWeight="bold" color="secondary">{value}</Typography>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Paper>
                          )}

                          {/* Sub-Industry Value Chain */}
                          {selectedSections.valueChainTable && (
                            <Paper
                              elevation={1}
                              sx={{
                                mt: 4,
                                mb: 3,
                                borderRadius: 3,
                                overflow: 'hidden',
                                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                                boxShadow: `0 8px 32px ${alpha(theme.palette.secondary.main, 0.15)}`
                              }}
                            >
                              <Box
                                sx={{
                                  p: 3,
                                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                                  color: 'white',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 70%)',
                                    pointerEvents: 'none'
                                  }
                                }}
                              >
                                <Typography variant="h4" fontWeight={600} sx={{ position: 'relative', zIndex: 1 }}>
                                  Value Chain
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.95, mt: 0.5, position: 'relative', zIndex: 1 }}>
                                  Model of the Value Chain. Each name is a separate page with data.
                                </Typography>
                              </Box>
                              <Box p={3}>
                                <ValueChainTable
                                  valueChainId={subIndustry.id}
                                  valueChainData={subIndustry.valueChain && subIndustry.valueChain.stages ? subIndustry.valueChain : null}
                                  industryName={subIndustry.name}
                                  industryId={undefined}
                                  subIndustryId={undefined}
                                  subIndustries={[]}
                                  onNavigate={() => {}}
                                />
                              </Box>
                            </Paper>
                          )}

                          {/* Sub-Industry Support Ecosystem */}
                          {(selectedSections.supportEcosystem || selectedSections.supportEcosystemSummary || selectedSections.supportEcosystemTalent || selectedSections.supportEcosystemFunding || selectedSections.supportEcosystemSupplyChain || selectedSections.supportEcosystemPlayers) && subIndustry.supportEcosystemRecord && (
                            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                              <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                                Support Ecosystem
                              </Typography>

                              {(selectedSections.supportEcosystem || selectedSections.supportEcosystemSummary) && subIndustry.supportEcosystemRecord.summary && (
                                <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6 }}>{subIndustry.supportEcosystemRecord.summary}</Typography>
                              )}

                              <Grid container spacing={3}>
                                {(selectedSections.supportEcosystem || selectedSections.supportEcosystemTalent) && subIndustry.supportEcosystemRecord.talent && Object.keys(subIndustry.supportEcosystemRecord.talent).length > 0 && (
                                  <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2, height: '100%' }}>
                                      <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                                        Talent Development
                                      </Typography>
                                      {Object.entries(subIndustry.supportEcosystemRecord.talent).map(([key, value]) => (
                                        <Typography key={key} variant="body2" sx={{ mt: 1 }}>
                                          <strong>{key.replace(/([A-Z])/g, ' $1').trim()}:</strong> {String(value)}
                                        </Typography>
                                      ))}
                                    </Box>
                                  </Grid>
                                )}

                                {(selectedSections.supportEcosystem || selectedSections.supportEcosystemFunding) && subIndustry.supportEcosystemRecord.funding && Object.keys(subIndustry.supportEcosystemRecord.funding).length > 0 && (
                                  <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 2, height: '100%' }}>
                                      <Typography variant="subtitle1" fontWeight="bold" color="info.main" gutterBottom>
                                        Funding & Investment
                                      </Typography>
                                      {Object.entries(subIndustry.supportEcosystemRecord.funding).map(([key, value]) => (
                                        <Typography key={key} variant="body2" sx={{ mt: 1 }}>
                                          <strong>{key.replace(/([A-Z])/g, ' $1').trim()}:</strong> {String(value)}
                                        </Typography>
                                      ))}
                                    </Box>
                                  </Grid>
                                )}

                                {(selectedSections.supportEcosystem || selectedSections.supportEcosystemSupplyChain) && subIndustry.supportEcosystemRecord.supplyChain && Object.keys(subIndustry.supportEcosystemRecord.supplyChain).length > 0 && (
                                  <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 2, height: '100%' }}>
                                      <Typography variant="subtitle1" fontWeight="bold" color="warning.main" gutterBottom>
                                        Supply Chain
                                      </Typography>
                                      {Object.entries(subIndustry.supportEcosystemRecord.supplyChain).map(([key, value]) => (
                                        <Typography key={key} variant="body2" sx={{ mt: 1 }}>
                                          <strong>{key.replace(/([A-Z])/g, ' $1').trim()}:</strong> {String(value)}
                                        </Typography>
                                      ))}
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>

                              {(selectedSections.supportEcosystem || selectedSections.supportEcosystemPlayers) && subIndustry.supportEcosystemRecord.keyPlayers && subIndustry.supportEcosystemRecord.keyPlayers.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                  <Typography variant="body2" fontWeight="bold" color="secondary" gutterBottom>Key Support Organizations:</Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                    {subIndustry.supportEcosystemRecord.keyPlayers.map((player, i) => (
                                      <Chip key={i} label={player} size="small" color="secondary" variant="outlined" />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Paper>
                          )}

                          {/* Sub-Industry Tabs Data */}
                          {subIndustry.tabs && (
                            <>
                              {selectedSections.marketOverview && subIndustry.tabs.marketOverview && (
                                <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.info.main}` }}>
                                  <Typography variant="h6" fontWeight="bold" gutterBottom color="info.main">
                                    Market Overview
                                  </Typography>
                                  {renderMarketOverview(subIndustry.tabs.marketOverview)}
                                </Paper>
                              )}

                              {selectedSections.legalInfrastructure && subIndustry.tabs.legalInfrastructure && (
                                <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.secondary.main}` }}>
                                  <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                                    Legal & Infrastructure
                                  </Typography>
                                  {renderLegalInfrastructure(subIndustry.tabs.legalInfrastructure)}
                                </Paper>
                              )}

                              {selectedSections.trends && subIndustry.tabs.trends && (
                                <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.success.main}` }}>
                                  <Typography variant="h6" fontWeight="bold" gutterBottom color="success.main">
                                    Market Trends
                                  </Typography>
                                  {renderTrends(subIndustry.tabs.trends)}
                                </Paper>
                              )}

                              {selectedSections.incentives && subIndustry.tabs.incentives && (
                                <Paper elevation={1} sx={{ p: 3, mb: 3, borderLeft: `4px solid ${theme.palette.warning.main}` }}>
                                  <Typography variant="h6" fontWeight="bold" gutterBottom color="warning.main">
                                    Government Incentives
                                  </Typography>
                                  {renderIncentives(subIndustry.tabs.incentives)}
                                </Paper>
                              )}
                            </>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* Print Header - Only visible when printing */}
      <Box className="print-header">
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', color: 'white !important', mb: 1 }}>
          VALUE CHAIN GLOBAL
        </Typography>
        <Typography variant="subtitle1" sx={{ color: 'white !important' }}>
          Industry Intelligence & Analysis Platform • Saudi Vision 2030
        </Typography>
      </Box>

      {/* Print Footer - Only visible when printing */}
      <Box className="print-footer">
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          VALUE CHAIN GLOBAL
        </Typography>
        <Typography variant="caption">
          www.value-chain.global • Connecting Industries • Empowering Growth
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          © {new Date().getFullYear()} Vision 2030 Initiative
        </Typography>
      </Box>
    </Container>
    </>
  );
};

export default IndustriesReportPage;